import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, vModelCheckbox as _vModelCheckbox, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "flex h-full min-h-0 flex-col gap-5 overflow-hidden p-4 pt-20 xl:p-8 xl:pt-8" };
const _hoisted_2 = { class: "shrink-0" };
const _hoisted_3 = { class: "text-2xl font-bold text-eblack-900" };
const _hoisted_4 = { class: "mt-1 text-sm text-eblack-500" };
const _hoisted_5 = {
    key: 0,
    class: "shrink-0 rounded-lg border border-amber-300 bg-amber-50 p-4"
};
const _hoisted_6 = { class: "text-sm font-semibold text-amber-900" };
const _hoisted_7 = { class: "mt-1 text-xs text-amber-800" };
const _hoisted_8 = { class: "mt-3 flex flex-wrap items-center gap-2" };
const _hoisted_9 = { class: "break-all rounded bg-white px-3 py-2 text-sm text-eblack-800" };
const _hoisted_10 = { class: "flex flex-col gap-1 text-sm font-medium text-eblack-700" };
const _hoisted_11 = ["placeholder"];
const _hoisted_12 = { class: "mt-3 flex items-center gap-2 text-sm font-medium text-eblack-700" };
const _hoisted_13 = {
    key: 0,
    class: "mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700"
};
const _hoisted_14 = { class: "mt-3 flex gap-2" };
const _hoisted_15 = ["disabled"];
const _hoisted_16 = {
    key: 2,
    class: "shrink-0 text-sm text-eblack-500"
};
const _hoisted_17 = {
    key: 3,
    class: "min-h-0 flex-1 overflow-y-auto rounded-lg border border-ewhite-200 bg-white shadow-sm"
};
const _hoisted_18 = { class: "flex flex-wrap items-start justify-between gap-3" };
const _hoisted_19 = { class: "break-all text-sm text-eblack-800" };
const _hoisted_20 = { class: "mt-1 text-xs text-eblack-500" };
const _hoisted_21 = {
    key: 0,
    class: "flex gap-2"
};
const _hoisted_22 = ["onClick"];
const _hoisted_23 = ["onClick"];
const _hoisted_24 = { class: "mt-2 text-sm text-eblack-600" };
const _hoisted_25 = {
    key: 0,
    class: "p-6 text-center text-sm text-eblack-500"
};
import { computed, onMounted, ref, watch } from "vue";
import { useToast } from "vue-toastification";
import { useAccessGrants } from "@/domains/access-grants";
import { useAuthStore } from "@/domains/auth";
import { i18n } from "@/i18n";
export default /*@__PURE__*/ _defineComponent({
    __name: 'WidgetKeysIndex',
    setup(__props) {
        const toast = useToast();
        const authStore = useAuthStore();
        const orgId = computed(() => (authStore.currentOrgId ? Number(authStore.currentOrgId) : null));
        const { grants, newlyIssuedGrant, isLoading, load, issue, revoke, acknowledgeNewlyIssuedGrant } = useAccessGrants();
        const originsText = ref("");
        const captchaRequired = ref(false);
        const reissuing = ref(null);
        const error = ref("");
        const saving = ref(false);
        // The API doesn't reliably populate `status` (it's absent on create and, so
        // far, on freshly-issued list entries), but `revokedAt` does get set once a
        // grant is revoked — so treat either signal as authoritative.
        function isRevoked(grant) {
            return grant.status === "revoked" || Boolean(grant.revokedAt);
        }
        function origins() {
            const values = originsText.value.split("\n").map((value) => value.trim()).filter(Boolean);
            const normalized = values.map((value) => {
                const url = new URL(value);
                if (!["http:", "https:"].includes(url.protocol) || url.username || url.password || url.pathname !== "/" || url.search || url.hash)
                    throw new Error(i18n.global.t("settings.widget_keys.origin_url_error"));
                return url.origin;
            });
            if (!normalized.length || normalized.length > 20 || new Set(normalized).size !== normalized.length)
                throw new Error(i18n.global.t("settings.widget_keys.origin_count_error"));
            return normalized;
        }
        async function loadGrants() {
            if (!orgId.value)
                return;
            try {
                await load(orgId.value);
            }
            catch {
                error.value = i18n.global.t("settings.widget_keys.load_error");
            }
        }
        async function submit() {
            if (!orgId.value)
                return;
            const previouslyReissuing = reissuing.value;
            if (previouslyReissuing && !window.confirm(i18n.global.t("settings.widget_keys.reissue_confirm")))
                return;
            try {
                saving.value = true;
                error.value = "";
                const allowedOrigins = origins();
                await issue(orgId.value, allowedOrigins, captchaRequired.value);
                if (previouslyReissuing)
                    await revoke(orgId.value, previouslyReissuing.id);
                reset();
            }
            catch (cause) {
                error.value = cause instanceof Error ? cause.message : i18n.global.t("settings.widget_keys.create_error");
            }
            finally {
                saving.value = false;
            }
        }
        function reissue(grant) {
            reissuing.value = grant;
            originsText.value = grant.allowedOrigins.join("\n");
            captchaRequired.value = grant.captchaRequired;
            error.value = "";
        }
        function reset() {
            reissuing.value = null;
            originsText.value = "";
            captchaRequired.value = false;
            error.value = "";
        }
        async function revokeGrant(grant) {
            if (!orgId.value)
                return;
            if (!window.confirm(i18n.global.t("settings.widget_keys.revoke_confirm")))
                return;
            try {
                await revoke(orgId.value, grant.id);
            }
            catch {
                error.value = i18n.global.t("settings.widget_keys.revoke_error");
            }
        }
        async function copy(value) {
            await navigator.clipboard.writeText(value);
            toast.success(i18n.global.t("settings.widget_keys.token_copied"));
        }
        watch(orgId, loadGrants);
        onMounted(loadGrants);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("h1", _hoisted_3, _toDisplayString(_ctx.$t("settings.widget_keys.title")), 1),
                    _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t("settings.widget_keys.description")), 1)
                ]),
                (_unref(newlyIssuedGrant))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t("settings.widget_keys.token_banner_title")), 1),
                        _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t("settings.widget_keys.token_banner_hint")), 1),
                        _createElementVNode("div", _hoisted_8, [
                            _createElementVNode("code", _hoisted_9, _toDisplayString(_unref(newlyIssuedGrant).token), 1),
                            _createElementVNode("button", {
                                type: "button",
                                class: "rounded border border-ewhite-300 bg-white px-3 py-1.5 text-sm",
                                onClick: _cache[0] || (_cache[0] = ($event) => (copy(_unref(newlyIssuedGrant).token)))
                            }, _toDisplayString(_ctx.$t("settings.widget_keys.copy")), 1)
                        ]),
                        _createElementVNode("button", {
                            type: "button",
                            class: "mt-3 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white",
                            onClick: _cache[1] || (_cache[1] =
                                //@ts-ignore
                                (...args) => (_unref(acknowledgeNewlyIssuedGrant) && _unref(acknowledgeNewlyIssuedGrant)(...args)))
                        }, _toDisplayString(_ctx.$t("settings.widget_keys.token_banner_dismiss")), 1)
                    ]))
                    : _createCommentVNode("", true),
                (orgId.value)
                    ? (_openBlock(), _createElementBlock("form", {
                        key: 1,
                        class: "shrink-0 rounded-lg border border-ewhite-200 bg-white p-4 shadow-sm",
                        onSubmit: _withModifiers(submit, ["prevent"])
                    }, [
                        _createElementVNode("label", _hoisted_10, [
                            _createTextVNode(_toDisplayString(_ctx.$t("settings.widget_keys.origins_label")) + " ", 1),
                            _withDirectives(_createElementVNode("textarea", {
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((originsText).value = $event)),
                                rows: "4",
                                placeholder: _ctx.$t('settings.widget_keys.origins_placeholder'),
                                class: "rounded-lg border border-ewhite-300 px-3 py-2 font-mono text-sm font-normal outline-none focus:border-egreen-500"
                            }, null, 8, _hoisted_11), [
                                [_vModelText, originsText.value]
                            ])
                        ]),
                        _createElementVNode("label", _hoisted_12, [
                            _withDirectives(_createElementVNode("input", {
                                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((captchaRequired).value = $event)),
                                type: "checkbox",
                                class: "rounded border-ewhite-300"
                            }, null, 512), [
                                [_vModelCheckbox, captchaRequired.value]
                            ]),
                            _createTextVNode(" " + _toDisplayString(_ctx.$t("settings.widget_keys.captcha_required_label")), 1)
                        ]),
                        (error.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(error.value), 1))
                            : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_14, [
                            _createElementVNode("button", {
                                type: "submit",
                                disabled: saving.value,
                                class: "rounded-lg bg-egreen-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                            }, _toDisplayString(_ctx.$t("settings.widget_keys.create")), 9, _hoisted_15),
                            (reissuing.value)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 0,
                                    type: "button",
                                    class: "rounded-lg border border-ewhite-300 px-4 py-2 text-sm",
                                    onClick: reset
                                }, _toDisplayString(_ctx.$t("settings.widget_keys.cancel")), 1))
                                : _createCommentVNode("", true)
                        ])
                    ], 32))
                    : _createCommentVNode("", true),
                (_unref(isLoading))
                    ? (_openBlock(), _createElementBlock("p", _hoisted_16, _toDisplayString(_ctx.$t("settings.widget_keys.loading")), 1))
                    : (_openBlock(), _createElementBlock("div", _hoisted_17, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(grants), (grant) => {
                            return (_openBlock(), _createElementBlock("div", {
                                key: grant.id,
                                class: "border-b border-ewhite-100 p-4 last:border-0"
                            }, [
                                _createElementVNode("div", _hoisted_18, [
                                    _createElementVNode("div", null, [
                                        _createElementVNode("code", _hoisted_19, _toDisplayString(grant.id), 1),
                                        _createElementVNode("p", _hoisted_20, [
                                            _createTextVNode(_toDisplayString(isRevoked(grant) ? _ctx.$t("settings.widget_keys.status_revoked") : _ctx.$t("settings.widget_keys.status_active")), 1),
                                            (grant.createdAt)
                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                    _createTextVNode(" · " + _toDisplayString(new Date(grant.createdAt).toLocaleString()), 1)
                                                ], 64))
                                                : _createCommentVNode("", true)
                                        ])
                                    ]),
                                    (!isRevoked(grant))
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                                            _createElementVNode("button", {
                                                type: "button",
                                                class: "rounded border border-ewhite-300 px-3 py-1.5 text-sm",
                                                onClick: ($event) => (reissue(grant))
                                            }, _toDisplayString(_ctx.$t("settings.widget_keys.reissue")), 9, _hoisted_22),
                                            _createElementVNode("button", {
                                                type: "button",
                                                class: "rounded border border-red-200 px-3 py-1.5 text-sm text-red-700",
                                                onClick: ($event) => (revokeGrant(grant))
                                            }, _toDisplayString(_ctx.$t("settings.widget_keys.revoke")), 9, _hoisted_23)
                                        ]))
                                        : _createCommentVNode("", true)
                                ]),
                                _createElementVNode("p", _hoisted_24, _toDisplayString(grant.allowedOrigins.join(", ")), 1)
                            ]));
                        }), 128)),
                        (_unref(grants).length === 0)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_25, _toDisplayString(_ctx.$t("settings.widget_keys.empty")), 1))
                            : _createCommentVNode("", true)
                    ]))
            ]));
        };
    }
});
